import { useCallback, useRef, useState } from 'react';

/**
 * One image the dialog can be paged onto: its label and the read that fills it. A photo is read
 * from the server one photo at a time, so the read is deferred until the image is actually shown.
 */
export interface ImageViewItem {
  /**
   * The label of the image on screen.
   */
  name?: string;
  /**
   * Reads the base64 bytes of the image; an absent result counts as a failure, because the stored
   * original may have gone missing.
   */
  read: () => Promise<string | undefined>;
  /**
   * Whether the censor declined this photo. Absent for an image that carries no verdict at all —
   * an enclosure, an answer — which is then shown without one.
   */
  declined?: boolean;
}

/**
 * One image opened full screen: the state an `ImageViewDialog` is driven by, and the ways of
 * filling it. A read that fails has to say so — the tile is otherwise a dead click.
 */
export interface ImageView {
  /**
   * The label of the image on screen.
   */
  name?: string;
  /**
   * The base64 bytes of the image, absent until the read has succeeded.
   */
  content?: string;
  /**
   * Whether the bytes are still being read.
   */
  isLoading: boolean;
  /**
   * Whether the read failed. The dialog stays open and says so, rather than dropping the sender
   * back to the page — one missing original must not end a walk through the whole list.
   */
  isFailed: boolean;
  /**
   * The verdict on the image on screen, absent for one that carries none. Paging changes it, so the
   * view reads it from here rather than from whatever was clicked.
   */
  declined?: boolean;
  /**
   * Whether the dialog belongs on screen. It opens while the read is under way, so a slow read is
   * visible instead of looking like nothing happened.
   */
  isOpen: boolean;
  /**
   * Whether the list the dialog was opened on holds an image before the one on screen.
   */
  hasPrevious: boolean;
  /**
   * Whether the list the dialog was opened on holds an image after the one on screen.
   */
  hasNext: boolean;
  /**
   * Opens the dialog and reads a single image into it.
   *
   * @param name The label of the image.
   * @param read Reads the base64 bytes of the image.
   */
  open: (name: string | undefined, read: () => Promise<string | undefined>) => Promise<void>;
  /**
   * Opens the dialog on one image of a list, so the rest can be paged through without closing it.
   *
   * @param items The images of the list, in the order they are shown on the page.
   * @param index Which of them to open.
   */
  openList: (items: ImageViewItem[], index: number) => Promise<void>;
  /**
   * Opens the dialog on bytes that are already at hand.
   *
   * @param name    The label of the image.
   * @param content The base64 bytes of the image.
   */
  show: (name: string | undefined, content: string | undefined) => void;
  /**
   * Shows the image before the one on screen, reading it if it has not been read yet.
   */
  previous: () => Promise<void>;
  /**
   * Shows the image after the one on screen.
   */
  next: () => Promise<void>;
  /**
   * Closes the dialog.
   */
  close: () => void;
}

/**
 * Drives an `ImageViewDialog`: keeps the image it shows, reports a read that failed, keeps the
 * dialog open with a spinner while the read is under way, and remembers the list the image was
 * opened from so the dialog can page along it.
 */
export const useImageView = (): ImageView => {
  const [items, setItems] = useState<ImageViewItem[]>([]);
  const [index, setIndex] = useState<number>(0);
  const [name, setName] = useState<string | undefined>(undefined);
  const [content, setContent] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isFailed, setFailed] = useState<boolean>(false);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [declined, setDeclined] = useState<boolean | undefined>(undefined);
  // Which read is still the one on screen.
  const requestRef = useRef<number>(0);
  // The image the next page steps from.
  const indexRef = useRef<number>(0);

  const readInto = useCallback(async (items: ImageViewItem[], index: number): Promise<void> => {
    const item: ImageViewItem | undefined = items[index];
    if (!item) {
      return;
    }
    requestRef.current += 1;
    const request: number = requestRef.current;
    indexRef.current = index;
    setIndex(index);
    setName(item.name);
    setDeclined(item.declined);
    setContent(undefined);
    setFailed(false);
    setLoading(true);
    try {
      const bytes: string | undefined = await item.read();
      // A read the sender has already paged away from reports nothing:
      // its bytes belong under a name that is no longer on screen,
      // and its failure is not the failure of what replaced it.
      if (requestRef.current !== request) {
        return;
      }
      if (!bytes) {
        throw new Error('The image carries no content.');
      }
      setContent(bytes);
    } catch {
      if (requestRef.current !== request) {
        return;
      }
      setContent(undefined);
      setFailed(true);
    } finally {
      if (requestRef.current === request) {
        setLoading(false);
      }
    }
  }, []);

  const openList = useCallback(
    async (items: ImageViewItem[], index: number): Promise<void> => {
      setItems(items);
      setOpen(true);
      await readInto(items, index);
    },
    [readInto],
  );

  const open = useCallback(
    async (name: string | undefined, read: () => Promise<string | undefined>): Promise<void> => {
      await openList([{ name: name, read: read }], 0);
    },
    [openList],
  );

  const show = useCallback((name: string | undefined, content: string | undefined): void => {
    // Bytes already at hand end whatever was being read,
    // so an enclosure opened while a photo is still on its way does not get that photo dropped on top of it.
    requestRef.current += 1;
    indexRef.current = 0;
    setItems([]);
    setIndex(0);
    setName(name);
    setDeclined(undefined);
    setContent(content);
    setFailed(false);
    setLoading(false);
    setOpen(true);
  }, []);

  const previous = useCallback(async (): Promise<void> => {
    await readInto(items, indexRef.current - 1);
  }, [items, readInto]);

  const next = useCallback(async (): Promise<void> => {
    await readInto(items, indexRef.current + 1);
  }, [items, readInto]);

  const close = useCallback((): void => {
    indexRef.current = 0;
    setItems([]);
    setIndex(0);
    setDeclined(undefined);
    setContent(undefined);
    setFailed(false);
    setLoading(false);
    setOpen(false);
  }, []);

  return {
    name: name,
    content: content,
    isLoading: isLoading,
    isFailed: isFailed,
    isOpen: isOpen,
    declined: declined,
    hasPrevious: index > 0,
    hasNext: index < items.length - 1,
    open: open,
    openList: openList,
    show: show,
    previous: previous,
    next: next,
    close: close,
  };
};
